import ReactMarkdown from "react-markdown";

const MarkdownRender = ({ 
  children, 
  variant = "default",
  className = "",
  style = {} 
}) => {
  // Base components configuration
  const baseComponents = {
    p: ({ children }) => (
      <p className="mb-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
        {children}
      </p>
    ),
    strong: ({ children }) => (
      <strong className="fw-bold">{children}</strong>
    ),
    em: ({ children }) => <em>{children}</em>,
    ul: ({ children }) => (
      <ul className="mb-3" style={{ paddingLeft: "20px" }}>
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="mb-3" style={{ paddingLeft: "20px" }}>
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="mb-2" style={{ lineHeight: "1.6" }}>
        {children}
      </li>
    ),
    code: ({ children }) => (
      <code className="bg-light px-1 rounded small">
        {children}
      </code>
    ),
    // Add missing markdown components
    h1: ({ children }) => (
      <h4 className="text-primary fw-bold mb-3 mt-2" style={{ fontSize: "18px" }}>
        {children}
      </h4>
    ),
    h2: ({ children }) => (
      <h5 className="text-primary fw-bold mb-3 mt-4" style={{ fontSize: "16px" }}>
        {children}
      </h5>
    ),
    h3: ({ children }) => (
      <h6 className="text-dark fw-bold mb-2 mt-3" style={{ fontSize: "15px" }}>
        {children}
      </h6>
    ),
    h4: ({ children }) => (
      <h6 className="text-dark fw-bold mb-2 mt-3" style={{ fontSize: "14px" }}>
        {children}
      </h6>
    ),
    h5: ({ children }) => (
      <h6 className="text-dark fw-bold mb-2 mt-3" style={{ fontSize: "13px" }}>
        {children}
      </h6>
    ),
    h6: ({ children }) => (
      <h6 className="text-dark fw-bold mb-2 mt-3" style={{ fontSize: "12px" }}>
        {children}
      </h6>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-start border-3 border-primary ps-3 py-2 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
        {children}
      </blockquote>
    ),
    hr: () => (
      <hr className="my-4" style={{ borderColor: '#dee2e6', borderWidth: '2px' }} />
    ),
    a: ({ children, href }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary">
        {children}
      </a>
    ),
    pre: ({ children }) => (
      <pre className="bg-light p-3 rounded mb-3" style={{ fontSize: "13px", overflow: "auto" }}>
        {children}
      </pre>
    ),
    // Enhanced Table components for markdown tables
    table: ({ children }) => (
      <div className="table-responsive mb-4">
        <table className="table table-striped table-hover table-bordered shadow-sm">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="table-dark">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody>
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr>
        {children}
      </tr>
    ),
    th: ({ children, align }) => (
      <th 
        className={`fw-bold bg-primary text-white ${align === 'left' ? 'text-start' : align === 'right' ? 'text-end' : 'text-center'}`} 
        style={{ fontSize: "14px", padding: "12px" }}
      >
        {children}
      </th>
    ),
    td: ({ children, align }) => (
      <td 
        className={`${align === 'left' ? 'text-start' : align === 'right' ? 'text-end' : 'text-center'}`} 
        style={{ fontSize: "13px", padding: "10px", verticalAlign: "middle" }}
      >
        {children}
      </td>
    ),
  };

  // Variant-specific configurations
  const variantConfigs = {
    // For content outlines and drafts
    content: {
      ...baseComponents,
      h1: ({ children }) => (
        <h4 className="text-primary fw-bold mb-3 mt-2" style={{ fontSize: "18px" }}>
          {children}
        </h4>
      ),
      h2: ({ children }) => (
        <h5 className="text-primary fw-bold mb-3 mt-4" style={{ fontSize: "16px" }}>
          {children}
        </h5>
      ),
      h3: ({ children }) => (
        <h6 className="text-dark fw-bold mb-2 mt-3" style={{ fontSize: "15px" }}>
          {children}
        </h6>
      ),
      p: ({ children }) => (
        <p className="mb-2" style={{ fontSize: "13px" }}>
          {children}
        </p>
      ),
      li: ({ children }) => (
        <li className="mb-1" style={{ fontSize: "13px" }}>
          {children}
        </li>
      ),
      ul: ({ children }) => (
        <ul className="mb-3" style={{ paddingLeft: "20px" }}>
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="mb-3" style={{ paddingLeft: "20px" }}>
          {children}
        </ol>
      ),
      // Enhanced table styling for content
      table: ({ children }) => (
        <div className="table-responsive mb-4">
          <table className="table table-striped table-hover table-bordered shadow-sm">
            {children}
          </table>
        </div>
      ),
      th: ({ children }) => (
        <th className="fw-bold text-center bg-primary text-white" style={{ fontSize: "14px", padding: "12px" }}>
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="text-center" style={{ fontSize: "13px", padding: "12px", verticalAlign: "middle" }}>
          {children}
        </td>
      ),
    },

    // For AI overview table cells (compact)
    tableCell: {
      ...baseComponents,
      h1: ({ children }) => (
        <h6 className="fw-bold mb-1">
          {children}
        </h6>
      ),
      h2: ({ children }) => (
        <h6 className="fw-bold mb-1">
          {children}
        </h6>
      ),
      h3: ({ children }) => (
        <h6 className="fw-bold mb-1">{children}</h6>
      ),
      // Compact table styling for table cells
      table: ({ children }) => (
        <div className="table-responsive mb-2">
          <table className="table table-sm table-bordered">
            {children}
          </table>
        </div>
      ),
      th: ({ children }) => (
        <th className="fw-bold text-center bg-light" style={{ fontSize: "12px", padding: "6px" }}>
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="text-center" style={{ fontSize: "11px", padding: "6px", verticalAlign: "middle" }}>
          {children}
        </td>
      ),
    },

    // For AI overview table cells with primary color headers
    tableCellPrimary: {
      ...baseComponents,
      h1: ({ children }) => (
        <h6 className="fw-bold text-primary mb-1">
          {children}
        </h6>
      ),
      h2: ({ children }) => (
        <h6 className="fw-bold text-primary mb-1">
          {children}
        </h6>
      ),
      h3: ({ children }) => (
        <h6 className="fw-bold mb-1">{children}</h6>
      ),
      // Compact table styling with primary headers
      table: ({ children }) => (
        <div className="table-responsive mb-2">
          <table className="table table-sm table-bordered">
            {children}
          </table>
        </div>
      ),
      th: ({ children }) => (
        <th className="fw-bold text-center bg-primary text-white" style={{ fontSize: "12px", padding: "6px" }}>
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="text-center" style={{ fontSize: "11px", padding: "6px", verticalAlign: "middle" }}>
          {children}
        </td>
      ),
    },

    // For full page displays
    page: {
      ...baseComponents,
      h1: ({ children }) => (
        <h4 className="text-primary fw-bold mb-3 mt-2" style={{ fontSize: "20px" }}>
          {children}
        </h4>
      ),
      h2: ({ children }) => (
        <h5 className="text-primary fw-bold mb-3 mt-4" style={{ fontSize: "18px" }}>
          {children}
        </h5>
      ),
      h3: ({ children }) => (
        <h6 className="text-dark fw-bold mb-2 mt-3" style={{ fontSize: "16px" }}>
          {children}
        </h6>
      ),
      ul: ({ children }) => (
        <ul className="mb-3" style={{ paddingLeft: "20px" }}>
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="mb-3" style={{ paddingLeft: "20px" }}>
          {children}
        </ol>
      ),
      li: ({ children }) => (
        <li className="mb-1" style={{ fontSize: "14px" }}>
          {children}
        </li>
      ),
      strong: ({ children }) => (
        <strong className="text-dark fw-bold">{children}</strong>
      ),
      p: ({ children }) => (
        <p className="mb-2" style={{ fontSize: "14px" }}>
          {children}
        </p>
      ),
      code: ({ children }) => (
        <code className="bg-light px-1 rounded" style={{ fontSize: "13px" }}>
          {children}
        </code>
      ),
      // Enhanced table styling for full page displays
      table: ({ children }) => (
        <div className="table-responsive mb-4">
          <table className="table table-striped table-hover table-bordered shadow">
            {children}
          </table>
        </div>
      ),
      th: ({ children }) => (
        <th className="fw-bold text-center bg-primary text-white" style={{ fontSize: "15px", padding: "15px" }}>
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="text-center" style={{ fontSize: "14px", padding: "12px", verticalAlign: "middle" }}>
          {children}
        </td>
      ),
    },

    // Default variant
    default: baseComponents
  };

  const components = variantConfigs[variant] || variantConfigs.default;

  // Debug: Log the content being processed
  if (typeof children === 'string' && children.includes('#')) {
    console.log('MarkdownRender processing:', { 
      variant, 
      contentLength: children.length,
      hasHeaders: children.includes('#'),
      hasLists: children.includes('-'),
      content: children.substring(0, 200) + '...' 
    });
  }

  return (
    <div className={className} style={style}>
      <ReactMarkdown components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRender;
